/**
 * StaffWorkspaceBadge — "Working at: [Host] · [Warehouse]" pill that
 * lives in the navbar so staff users always see what context they're
 * operating in, and can switch warehouse if they have access to more
 * than one.
 *
 * Renders nothing for non-staff. For staff with a single warehouse
 * the warehouse name is shown read-only; with multiple, it becomes
 * a dropdown.
 *
 * For staff with multiple host memberships (rare), the host name
 * is also a dropdown so they can switch employer in-session.
 *
 * Visual: small light-orange chip + white pill with host/warehouse
 * names. Clicking the warehouse name opens a popover with the picker.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FiBriefcase, FiChevronDown, FiHome, FiMapPin } from 'react-icons/fi';
import { useStaffWorkspace } from './useStaffWorkspace';
import { useBranding } from '../../contexts/BrandingContext';

const StaffWorkspaceBadge = () => {
  const branding = useBranding();
  const primary = branding.primary_color || '#FF8A4C';
  const ws = useStaffWorkspace();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  // Close on outside click. Mounting the listener only when the
  // popover is open keeps the cost off the critical render path.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!ws.isStaff) return null;
  if (ws.loading) {
    // Skeleton — keeps the navbar height stable so other items don't reflow.
    return (
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 10,
          background: '#f3f4f6',
          minWidth: 180,
          fontSize: '0.78rem', color: '#9ca3af',
        }}
      >
        Loading workspace…
      </div>
    );
  }

  // Staff with no active memberships — likely a misconfigured account.
  // Surface it as a warning rather than silently rendering nothing,
  // which would leave the user wondering why the badge is missing.
  if (ws.memberships.length === 0) {
    return (
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 10,
          background: '#fef3c7', color: '#92400e',
          fontSize: '0.78rem', fontWeight: 600,
        }}
      >
        Workspace not configured · contact your admin
      </div>
    );
  }

  const activeWarehouse = ws.warehouses.find((w) => w.id === ws.warehouseId);
  const hostName = ws.activeHost?.organization_name
    || ws.activeHost?.full_name
    || 'Unknown host';
  const multipleHosts = ws.memberships.length > 1;
  const multipleWarehouses = ws.warehouses.length > 1;

  return (
    <div
      ref={popoverRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => (multipleHosts || multipleWarehouses) && setOpen((v) => !v)}
        title={multipleHosts || multipleWarehouses
          ? 'Click to switch host or warehouse'
          : `Working at ${hostName}${activeWarehouse ? ` · ${activeWarehouse.label}` : ''}`}
        disabled={!(multipleHosts || multipleWarehouses)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderRadius: 10,
          background: '#fff',
          border: `1px solid ${primary}40`,
          color: '#1a1a2e',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: (multipleHosts || multipleWarehouses) ? 'pointer' : 'default',
          transition: 'border-color .15s ease',
        }}
      >
        <span style={{
          width: 22, height: 22, borderRadius: 6,
          background: primary, color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <FiBriefcase size={11} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <span style={{ fontSize: '0.62rem', color: '#9ca3af', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Working at
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 240 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {hostName}
            </span>
            {activeWarehouse && (
              <>
                <span style={{ color: '#cbd5e1' }}>·</span>
                <span
                  style={{
                    color: primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 120,
                  }}
                >
                  {activeWarehouse.label}
                </span>
              </>
            )}
            {(multipleHosts || multipleWarehouses) && (
              <FiChevronDown size={12} style={{ flexShrink: 0, color: '#9ca3af' }} />
            )}
          </span>
        </span>
      </button>

      {open && (multipleHosts || multipleWarehouses) && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 280,
            maxWidth: 360,
            background: '#fff',
            border: '1px solid #e7eaf2',
            borderRadius: 12,
            boxShadow: '0 14px 36px rgba(15, 18, 38, 0.14)',
            padding: 12,
            zIndex: 1050,
          }}
        >
          {/* Host switcher — only when memberships > 1 */}
          {multipleHosts && (
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#6b7280', marginBottom: 6,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <FiHome size={11} /> Host
              </div>
              {ws.memberships.map((m) => {
                const active = m.host_id === ws.activeHostId;
                const name = m.host?.organization_name || m.host?.full_name || 'Unknown';
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { ws.setActiveHost(m.host_id); }}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '8px 10px', borderRadius: 8,
                      border: 'none',
                      background: active ? `${primary}1a` : 'transparent',
                      color: active ? primary : '#1a1a2e',
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.86rem',
                      cursor: 'pointer',
                      marginBottom: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </span>
                    {active && (
                      <span style={{ fontSize: '0.66rem', color: primary, fontWeight: 700 }}>
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Warehouse picker — always shown if 2+ warehouses available */}
          {multipleWarehouses && (
            <div>
              <div style={{
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#6b7280', marginBottom: 6,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <FiMapPin size={11} /> Warehouse
              </div>
              {ws.warehouses.map((w) => {
                const active = w.id === ws.warehouseId;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => { ws.setSelectedWarehouse(w.id); setOpen(false); }}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '8px 10px', borderRadius: 8,
                      border: 'none',
                      background: active ? `${primary}1a` : 'transparent',
                      color: active ? primary : '#1a1a2e',
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.86rem',
                      cursor: 'pointer',
                      marginBottom: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {w.label}
                      {w.city && (
                        <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>
                          · {w.city}
                        </span>
                      )}
                    </span>
                    {active && (
                      <span style={{ fontSize: '0.66rem', color: primary, fontWeight: 700 }}>
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffWorkspaceBadge;
